<template>
  <div id="client-area-content">
    <div class="content-container">
      <!-- Columna izquierda -->
      <div class="left-column">
        <!-- Sección Comerciales -->
        <div class="section comerciales-section">
          <div class="section-header">
            <div class="icon-comerciales">
              <img :src="comercialesIconSvg" alt="comerciales" />
            </div>
            <h3 class="section-title">
              {{ $t('clientAreaPage.assignedCommercial') }}
            </h3>
          </div>
          <div class="section-content">
            <!-- En empleados puede tardar la consulta del comercial; mientras
                 tanto se ocultan los datos anteriores de otra compañía. -->
            <div v-if="commercialLoading" class="commercial-loading">
              <f7-preloader></f7-preloader>
              <span>{{ $t('loading') }}</span>
            </div>
            <CommercialData
              v-else-if="commercials.length > 0"
              :commercial="commercials[0]"
            />
          </div>
        </div>

        <!-- Sección Servicios -->
        <div class="section servicios-section">
          <div class="section-header">
            <div class="header-left">
              <div class="icon-servicios">
                <img :src="serviciosIconSvg" alt="servicios" />
              </div>
              <h3 class="section-title">
                {{ $t('clientAreaPage.actualServices') }}
              </h3>
            </div>
            <div class="header-right">
              <button class="btn-primary" @click="toOffersPage">
                {{ $t('clientAreaPage.contract') }}
              </button>
            </div>
          </div>
          <div class="section-content">
            <div v-if="servicesLoading" class="services-preloader">
              <f7-preloader></f7-preloader>
              <span>{{ $t('loading') }}</span>
            </div>
            <template v-else>
              <div
                v-for="service in dynamicServices"
                :key="service.type"
                class="service-item"
              >
                <div class="service-info">
                  <div class="service-icon">
                    <img
                      :src="service.icon"
                      :alt="$t(service.translationKey)"
                    />
                  </div>
                  <span class="service-name">
                    {{ $t(service.translationKey) }}
                  </span>
                </div>
                <div class="service-description">
                  {{ $t('samples') }}
                </div>
              </div>
              <div
                v-if="dynamicServices.length === 0"
                class="no-services"
              >
                <p>
                  {{ $t('clientAreaPage.no_services_active') }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Columna derecha - Gestión de usuarios -->
      <div class="right-column tableContainer">
        <UsersDataTable />
      </div>
    </div>
  </div>
</template>

<script>
import Api from '@/services/Api';
import { mapGetters, mapState, mapActions } from 'vuex';
// Components
import CommercialData from '@/components/ClientArea/commercialData.vue';
import UsersDataTable from '@/components/UsersTable/index.vue';
// Icons
import comercialesIconSvg from '@/static/icons/comerciales.svg';
import serviciosIconSvg from '@/static/icons/servicios.svg';
import ambientalIconSvg from '@/static/icons/ambiental-2.svg';
import alimentariaIconSvg from '@/static/icons/alimentaria-2.svg';
import agriculturaIconSvg from '@/static/icons/agricultura.svg';
import gestionIconSvg from '@/static/icons/gestion_usuarios.svg';
import mineriaIconSvg from '@/static/icons/mineria.svg';
import { NOTIFY_TIME_LONG } from '@/js/constants';

export default {
  name: 'ClientArea',
  components: {
    CommercialData,
    UsersDataTable,
  },
  data() {
    return {
      historicalAtcs: [],
      servicesLoading: true,
      // Mapeo de valores de BD (español) a claves de traducción
      serviceTypeTranslationMap: {
        Agronomía: 'Agronomy',
        Alimentaria: 'Food',
        'Medio Ambiente': 'Environment',
        Minería: 'Mining',
        'Salud y Seguridad': 'Health and Security',
        // También mapear nombres alternativos que puedan venir
        Agricultura: 'Agronomy',
        Ambiental: 'Environment',
      },
    };
  },
  computed: {
    ...mapGetters('contact', ['contactInfo']),
    ...mapState('samples', ['aggregateInfo']),
    ...mapState('contact', ['company', 'address']),
    comercialesIconSvg: () => comercialesIconSvg,
    serviciosIconSvg: () => serviciosIconSvg,
    ambientalIconSvg: () => ambientalIconSvg,
    alimentariaIconSvg: () => alimentariaIconSvg,
    agriculturaIconSvg: () => agriculturaIconSvg,
    mineriaIconSvg: () => mineriaIconSvg,
    gestionIconSvg: () => gestionIconSvg,
    commercialLoading() {
      return Boolean(this.contactInfo?.commercialLoading);
    },
    commercials() {
      // La información llega del getter global y se recalcula al cambiar la
      // compañía activa, evitando mantener una copia local obsoleta.
      const commercial = {
        name: this.contactInfo?.agent,
        email: this.contactInfo?.agent_email,
      };

      return commercial.name || commercial.email ? [commercial] : [];
    },

    // Mapear aggregateInfo a servicios dinámicos
    dynamicServices() {
      if (
        !this.aggregateInfo ||
        this.aggregateInfo.length === 0 ||
        !this.historicalAtcs.length
      ) {
        return [];
      }

      const services = [];
      const visibleAtcs = new Set(this.historicalAtcs);

      // Mapear cada tipo de servicio con sus datos
      for (const item of this.aggregateInfo) {
        const serviceType = Object.keys(item)[0];

        if (!visibleAtcs.has(serviceType)) {
          continue;
        }

        let iconSvg;
        let translationKey;

        switch (serviceType) {
          case 'Agronomía':
            iconSvg = this.agriculturaIconSvg;
            translationKey = 'Agronomy';
            break;
          case 'Alimentaria':
            iconSvg = this.alimentariaIconSvg;
            translationKey = 'Food';
            break;
          case 'Medio Ambiente':
            iconSvg = this.ambientalIconSvg;
            translationKey = 'Environment';
            break;
          case 'Minería':
            iconSvg = this.mineriaIconSvg;
            translationKey = 'Mining';
            break;
          case 'Salud y Seguridad':
            iconSvg = this.ambientalIconSvg;
            translationKey = 'Health and Security';
            break;
          default:
            // Icono y nombre por defecto
            iconSvg = this.ambientalIconSvg;
            // Intentar obtener la clave de traducción del mapa, o usar el valor original
            translationKey =
              this.serviceTypeTranslationMap[serviceType] ||
              serviceType;
        }

        services.push({
          type: serviceType,
          icon: iconSvg,
          translationKey,
        });
      }

      return services;
    },
  },
  async beforeMount() {
    try {
      await this.getAggregateInfo();
      await this.loadHistoricalAtcs();
    } catch (e) {
      const errorMessage = this.getErrorMessage(e);

      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(errorMessage)
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.servicesLoading = false;
    }
  },
  methods: {
    ...mapActions('samples', ['getAggregateInfo']),
    parseJson(value) {
      if (typeof value !== 'string' || !value.trim()) {
        return null;
      }

      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    },
    getErrorMessage(error) {
      const parsedMessage = this.parseJson(error?.message);
      const parsedResponse = this.parseJson(error?.response);

      return (
        parsedMessage?.error ||
        parsedResponse?.error ||
        error?.message ||
        'unexpected_error'
      );
    },
    async loadHistoricalAtcs() {
      const xhr = await Api.getHistoricalAtcs(
        this.company,
        this.address
      );
      const response = this.parseJson(xhr?.response);

      if (Array.isArray(response)) {
        this.historicalAtcs = response;
        return;
      }

      if (Array.isArray(response?.data)) {
        this.historicalAtcs = response.data;
        return;
      }

      if (Array.isArray(response?.atcs)) {
        this.historicalAtcs = response.atcs;
        return;
      }

      this.historicalAtcs = [];
    },
    toOffersPage() {
      this.$f7.views.main.router.navigate({
        name: 'offersList',
      });
    },
  },
};
</script>

<style lang="scss">
@import '@/components/ClientArea/clientArea.scss';
</style>
